import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Globe, ArrowUpRight, Linkedin, Instagram, Facebook, Youtube, Twitter } from "lucide-react";
import { BRAND, NAV, PRODUCTS, IMAGES } from "@/lib/site";

const SOCIALS = [
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Facebook, href: "#", label: "Facebook" },
  { icon: Youtube, href: "#", label: "YouTube" },
  { icon: Twitter, href: "#", label: "Twitter" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative bg-charcoal text-white overflow-hidden">
      <div className="grain absolute inset-0 opacity-50" />
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-stenzia-green/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-20 w-[500px] h-[500px] rounded-full bg-stenzia-gold/5 blur-3xl pointer-events-none" />

      {/* CTA banner */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="overline text-stenzia-gold">Let&apos;s engineer your next showroom</div>
            <h3 className="mt-3 font-display font-light text-2xl md:text-4xl tracking-tight leading-tight">
              Premium display systems, <span className="italic text-stenzia-gold">tailored to your brand.</span>
            </h3>
          </div>
          <Link to="/contact" data-testid="footer-cta" className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-charcoal text-sm font-medium hover:bg-stenzia-gold transition-colors">
            Talk to our engineers <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <img src={IMAGES.logo} alt="Stenzia Displays" className="w-20 h-20 object-contain" />
            </div>
            <p className="mt-6 text-white/55 leading-relaxed max-w-sm">
              Engineering premium tile display systems from Morbi, India — trusted by showrooms, architects and tile brands worldwide.
            </p>

            <div className="mt-8 flex flex-col gap-3 text-sm text-white/70">
              <div className="text-[10px] tracking-[0.22em] uppercase text-stenzia-gold font-semibold">Sales &amp; Support</div>
              <a href={`tel:${BRAND.phone.replace(/\s+/g, "")}`} data-testid="footer-phone-primary" className="flex items-center gap-3 hover:text-white transition-colors group">
                <Phone size={14} className="text-stenzia-gold" /> <span className="group-hover:translate-x-0.5 transition-transform">{BRAND.phone}</span>
              </a>
              <a href={`tel:${BRAND.phoneSecondary.replace(/\s+/g, "")}`} data-testid="footer-phone-secondary" className="flex items-center gap-3 hover:text-white transition-colors group">
                <Phone size={14} className="text-stenzia-gold" /> <span className="group-hover:translate-x-0.5 transition-transform">{BRAND.phoneSecondary}</span>
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="footer-email" className="flex items-center gap-3 hover:text-white transition-colors group">
                <Mail size={14} className="text-stenzia-gold" /> <span className="group-hover:translate-x-0.5 transition-transform">{BRAND.email}</span>
              </a>
              <div className="flex items-center gap-3"><MapPin size={14} className="text-stenzia-gold" /> {BRAND.address}</div>
                <a
                  href={`https://${BRAND.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:text-white transition-colors group"
                >
                  <Globe size={14} className="text-stenzia-gold" />
                  <span className="group-hover:translate-x-0.5 transition-transform">
                    {BRAND.website}
                  </span>
                </a>            </div>

            <div className="mt-8 flex items-center gap-2">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-stenzia-green hover:text-white hover:border-stenzia-green hover:scale-110 transition-all duration-500"
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="overline text-stenzia-gold">Navigate</h4>
            <ul className="mt-5 space-y-3">
              {NAV.map((n) => (
                <li key={n.path}>
                  <Link to={n.path} className="text-white/65 hover:text-white transition-colors text-sm inline-flex items-center gap-1 group">
                    <span className="w-0 group-hover:w-3 h-px bg-stenzia-gold transition-all duration-300" />
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline text-stenzia-gold">Products</h4>
            <ul className="mt-5 space-y-3">
              {PRODUCTS.slice(0, 6).map((p) => (
                <li key={p.slug}>
                  <Link to="/products" className="text-white/65 hover:text-white transition-colors text-sm inline-flex items-center gap-1 group">
                    <span className="w-0 group-hover:w-3 h-px bg-stenzia-gold transition-all duration-300" />
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline text-stenzia-gold">Global Reach</h4>
            <p className="mt-5 text-white/55 text-sm leading-relaxed">Serving clients across India, UAE, Europe, USA, Australia and South-East Asia.</p>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {["IN", "AE", "EU", "US", "AU", "SG"].map((c) => (
                <div key={c} className="text-center py-2 rounded-lg bg-white/5 border border-white/10 text-xs tracking-widest text-white/70 transition-all duration-300 hover:bg-stenzia-green hover:border-stenzia-green hover:text-white cursor-default">{c}</div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {year} {BRAND.name}. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="inline-flex items-center gap-1.5 text-white/55"><span className="w-1 h-1 rounded-full bg-stenzia-gold" /> Designed &amp; Manufactured in India</span>
            <span>{BRAND.tagline}</span>
            <Link
  to="/privacy-policy"
  className="hover:text-white/80 transition-colors"
>
  Privacy
</Link>

<Link
  to="/terms-and-conditions"
  className="hover:text-white/80 transition-colors"
>
  Terms
</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
